/**
 * `useMeasureTool` — React state machine for the in-canvas measurement
 * overlay (WP-6 §8.6). Pulled out of `Giro3DToolbar.tsx` so the slim
 * toolbar stays under the 12 KB budget mandated by the masterplan.
 *
 * The hook owns the live measurement results, the cursor-snap pixel
 * coordinates, and the DOM listeners that drive the raycaster. It
 * delegates every Three.js side-effect to `measureController.ts`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addMarkerAtPoint,
  clearMeasureOverlays,
  createOverlayTracker,
  drawLineBetween,
  formatMeasureDistance,
  raycastAtEvent,
  type MeasurePoint3,
  type MeasureSurfaces,
} from './measureController';

export type MeasureMode = 'none' | 'distance' | 'height' | 'point';

export interface MeasureResult {
  readonly type: MeasureMode | 'point' | 'distance' | 'height';
  readonly value: string;
}

export interface UseMeasureToolApi {
  readonly mode: MeasureMode;
  readonly results: ReadonlyArray<MeasureResult>;
  readonly pointsCollected: number;
  readonly cursorSnap: { x: number; y: number } | null;
  start(mode: MeasureMode): void;
  stop(): void;
  clear(): void;
}

interface MeasurePointInternal {
  world: MeasurePoint3;
  snapped: boolean;
}

export function useMeasureTool(
  getSurfaces: () => MeasureSurfaces | null,
): UseMeasureToolApi {
  const [mode, setMode] = useState<MeasureMode>('none');
  const [results, setResults] = useState<MeasureResult[]>([]);
  const [points, setPoints] = useState<MeasurePointInternal[]>([]);
  const [cursorSnap, setCursorSnap] = useState<{ x: number; y: number } | null>(
    null,
  );
  const clickRef = useRef<((e: MouseEvent) => void) | null>(null);
  const moveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const overlayRef = useRef(createOverlayTracker());

  const finish = useCallback((domEl: HTMLElement) => {
    domEl.style.cursor = '';
    setMode('none');
    setCursorSnap(null);
    if (clickRef.current) {
      domEl.removeEventListener('click', clickRef.current);
      clickRef.current = null;
    }
    if (moveRef.current) {
      domEl.removeEventListener('mousemove', moveRef.current);
      moveRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    const s = getSurfaces();
    if (s?.domEl) finish(s.domEl);
    setPoints([]);
  }, [getSurfaces, finish]);

  const start = useCallback(
    (next: MeasureMode) => {
      stop();
      if (next === 'none') return;
      const s = getSurfaces();
      if (!s?.domEl) return;
      setMode(next);
      setPoints([]);
      s.domEl.style.cursor = 'crosshair';

      const onMove = async (event: MouseEvent) => {
        const r = await raycastAtEvent(s, event);
        setCursorSnap(r?.snapped ? { x: event.clientX, y: event.clientY } : null);
      };
      moveRef.current = onMove;
      s.domEl.addEventListener('mousemove', onMove);

      const onClick = async (event: MouseEvent) => {
        event.stopPropagation();
        const r = await raycastAtEvent(s, event);
        if (!r) return;
        await addMarkerAtPoint(overlayRef.current, s, r.point, r.snapped);
        setPoints((prev) => {
          const updated: MeasurePointInternal[] = [
            ...prev,
            { world: r.point, snapped: r.snapped },
          ];
          if (next === 'point') {
            const [x, y, z] = r.point;
            const snap = r.snapped ? ' (snapped)' : '';
            setResults((m) => [
              ...m,
              {
                type: 'point',
                value: `X: ${x.toFixed(3)}, Y: ${y.toFixed(3)}, Z: ${z.toFixed(3)}${snap}`,
              },
            ]);
            finish(s.domEl);
            return [];
          }
          if (updated.length === 2) {
            const [p1, p2] = updated;
            const dx = p2.world[0] - p1.world[0];
            const dy = p2.world[1] - p1.world[1];
            const dz = p2.world[2] - p1.world[2];
            const value =
              next === 'height'
                ? formatMeasureDistance(Math.abs(dz))
                : formatMeasureDistance(Math.sqrt(dx * dx + dy * dy + dz * dz));
            void drawLineBetween(overlayRef.current, s, p1.world, p2.world);
            setResults((m) => [...m, { type: next, value }]);
            finish(s.domEl);
            return [];
          }
          return updated;
        });
      };
      clickRef.current = onClick;
      s.domEl.addEventListener('click', onClick);
    },
    [getSurfaces, stop, finish],
  );

  const clear = useCallback(() => {
    stop();
    void clearMeasureOverlays(overlayRef.current, getSurfaces());
    setResults([]);
  }, [stop, getSurfaces]);

  useEffect(() => {
    const ref = overlayRef;
    return () => {
      stop();
      void clearMeasureOverlays(ref.current, getSurfaces());
    };
  }, [stop, getSurfaces]);

  return {
    mode,
    results,
    pointsCollected: points.length,
    cursorSnap,
    start,
    stop,
    clear,
  };
}
